import { FormProvider } from '@formily/react';
import { Form } from '@formily/antd-v5';
import {
  createForm,
  type Form as FormType,
  type IFormProps,
} from '@formily/core';
import { Button, Space } from 'antd';
import { useMemo, useRef } from 'react';

import type { IFormilySchema } from '../../types';
import { findFormComponent } from '../../utils/findFormComponent';
import { injectRequiredValidators } from '../../utils/injectRequiredValidators';
import { requiredIgnoringDeletedEffect } from '../../utils/formEffects';
import { parseJSONSchema } from '../../utils/data-parser';
import { useTranslate } from '../../../translate';
import xReactionParser from '../../utils/xReactionParser';
import { escapeFormItemsSchemas } from '../../utils';
import createSchemaFields from '../../utils/createSchemaFields';
import styled from 'styled-components';
import { stripSkipOnSubmitFields } from '../../utils/stripSkipOnSubmitFields';
import { useReactionScope } from '../../hooks/useReactionScope';

interface IAddRowFormProps {
  parentForm: FormType;
  schema: IFormilySchema;
  onSubmit: (values: Record<string, unknown>) => void;
  onCancel: () => void;
  initialValues?: Record<string, unknown>;
  context: Record<string, unknown>;
  cancelButtonText?: string;
  saveButtonText?: string;
  maxHeight?: string;
  disabled?: boolean;
  rowItemName?: string;
}

const AddRowForm: React.FC<IAddRowFormProps> = ({
  schema,
  onSubmit,
  onCancel,
  initialValues = {},
  parentForm,
  context,
  cancelButtonText = 'Cancel',
  saveButtonText = 'Save',
  maxHeight,
  disabled = false,
  rowItemName = 'row',
}) => {
  const FormSchemaField = useRef(createSchemaFields()).current;
  const reactionScope = useReactionScope();

  const { t } = useTranslate();

  const initialValuesLocal = useMemo(() => {
    if (Object.values(initialValues).length === 0) {
      return {
        _formLocal: true,
      };
    }

    return initialValues;
  }, [initialValues]);

  const formSchema = findFormComponent(schema);

  const parsedSchema = useMemo(() => {
    if (formSchema)
      return parseJSONSchema(
        formSchema,
        {
          ...context,
          [rowItemName]: initialValuesLocal,
        },
        { t }
      );
    return formSchema;
  }, [schema, context, t, rowItemName, initialValuesLocal]);

  const processedSchema = useMemo(
    () =>
      escapeFormItemsSchemas(xReactionParser(parsedSchema as IFormilySchema)),
    [parsedSchema]
  );

  const form = createForm({
    initialValues,
    parent: parentForm,
    context,
    disabled:
      disabled || processedSchema['x-component-props']?.editable === false,
    effects: requiredIgnoringDeletedEffect,
  } as IFormProps<any>);

  const handleSubmit = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();

    try {
      await form.submit();
      const values = stripSkipOnSubmitFields(form);
      onSubmit(values);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    onCancel();
  };

  if (!processedSchema) {
    return <div>Invalid schema: Form component not found</div>;
  }

  const schemaWithValidators = injectRequiredValidators(processedSchema);

  return (
    <FormProvider form={form}>
      <Form form={form} layout="vertical">
        <Scroller maxHeight={maxHeight}>
          <FormSchemaField
            schema={schemaWithValidators as any}
            scope={reactionScope}
          />
        </Scroller>
        <Space
          style={{ width: '100%', justifyContent: 'flex-end', padding: 24 }}
        >
          <Button onClick={handleCancel}>{cancelButtonText}</Button>
          {!disabled && (
            <Button type="primary" onClick={handleSubmit}>
              {saveButtonText}
            </Button>
          )}
        </Space>
      </Form>
    </FormProvider>
  );
};

const Scroller = styled.div<{ maxHeight?: string }>`
  padding: 24px;
  max-height: ${({ maxHeight }) => maxHeight};
  overflow: auto;
`;

export default AddRowForm;
